import { formatDayShort } from './dateUtils';

export const REVIEWED_STATUSES = ['approved', 'auto_approved'];

const INVALID_EXECUTION_META = {
    label: 'Stopped retrying',
    color: 'error',
    description: 'This was tried several times and failed every time, so it will not be tried again. Someone will need to do it by hand.',
};

export const SUGGESTION_STATUS_META = {
    pending: {
        label: 'Needs review',
        color: 'warning',
        description: 'Waiting for someone to approve or reject it.',
    },
    rejected: {
        label: 'Rejected',
        color: 'error',
        description: 'Turned down. It will not be carried out.',
    },
    approved: {
        not_started: {
            label: 'Approved',
            color: 'info',
            description: 'Approved and about to be carried out.',
        },
        executing: {
            label: 'Running',
            color: 'info',
            description: 'Being carried out right now — this only takes a moment.',
        },
        executed: {
            label: 'Done',
            color: 'success',
            description: 'Approved and carried out successfully.',
        },
        failed: {
            label: 'Failed',
            color: 'error',
            description: 'It was approved but could not be carried out. Fix the cause and try again.',
        },
        invalid: INVALID_EXECUTION_META,
    },
    auto_approved: {
        not_started: {
            label: 'Approved automatically',
            color: 'info',
            description: 'The assistant approved this on its own. It is about to be carried out.',
        },
        executing: {
            label: 'Running',
            color: 'info',
            description: 'The assistant is carrying this out right now — it only takes a moment.',
        },
        executed: {
            label: 'Done automatically',
            color: 'success',
            description: 'The assistant carried this out on its own. Kept as a record — nothing for you to do.',
        },
        failed: {
            label: 'Failed',
            color: 'error',
            description: 'The assistant tried to carry this out on its own but could not. Fix the cause and try again.',
        },
        invalid: INVALID_EXECUTION_META,
    },
};

export const SUGGESTION_ACTION_LABEL = {
    add_comment: 'Post a comment',
    create_task: 'Create a task',
    create_project: 'Create a project',
    complete_task: 'Complete a task',
    assign_responsible: 'Set who is responsible',
    update_assignees: 'Change who is assigned',
    change_status: 'Change the status',
    change_priority: 'Change the priority',
    change_due_date: 'Change the due date',
    add_blocker: 'Mark as blocked',
    create_qa_card: 'Add QA cards',
    send_reminder: 'Send a reminder',
};

export const SUGGESTION_TARGET_LABEL = {
    task: 'Task',
    project: 'Project',
    account: 'Account',
    quote: 'Quote',
};

export const SUGGESTION_RISK_META = {
    low: {
        label: 'Low risk',
        color: 'success',
        description: 'This is easy to undo if it turns out to be wrong.',
    },
    normal: {
        label: 'Changes real work',
        color: 'warning',
        description: 'This changes live data that people rely on. Read it through before approving.',
    },
};

export const riskMeta = (risk) => SUGGESTION_RISK_META[risk] || SUGGESTION_RISK_META.normal;

// A suggestion with no target is creating something; the resolved row points at where it lands.
export const targetHeading = (actionType, target) => {
    if (!target) return 'Applies to';
    if (target.origin === 'scope') {
        if (actionType === 'create_task' || actionType === 'create_project') return 'Will be created in';
        return 'Related to';
    }
    return 'Applies to';
};

export const targetTypeLabel = (type) => SUGGESTION_TARGET_LABEL[type] || type || 'Item';

export const targetDisplayName = (target) => {
    if (!target) return null;
    if (!target.exists) return `This ${targetTypeLabel(target.type).toLowerCase()} no longer exists`;
    return target.name || `Untitled ${targetTypeLabel(target.type).toLowerCase()}`;
};

export const targetLink = (target) => {
    if (!target || !target.exists || !target.accountSlug) return null;
    if (target.type === 'account') return `/account/${target.accountSlug}`;
    if (!target.id) return null;
    if (target.type === 'project') return `/account/${target.accountSlug}/project/${target.id}`;
    if (target.type === 'task') return target.projectId ? `/account/${target.accountSlug}/project/${target.projectId}/task/${target.id}/board` : null;
    return null;
};

export const statusMeta = (status, executionState) => {
    if (REVIEWED_STATUSES.includes(status)) {
        const byState = SUGGESTION_STATUS_META[status];
        return byState[executionState] || byState.not_started;
    }
    return SUGGESTION_STATUS_META[status] || { label: status || 'Unknown', color: 'default', description: '' };
};

export const actionLabel = (actionType) =>
    SUGGESTION_ACTION_LABEL[actionType] || (actionType ? actionType.replace(/_/g, ' ') : 'Unknown action');

export const isReviewable = (status, executionState) => status === 'pending' || (REVIEWED_STATUSES.includes(status) && executionState === 'failed');

// Rejecting an already-approved row would overwrite the review outcome the training loop reads,
// so the API accepts a reject only while the suggestion is still un-reviewed.
export const isRejectable = (status) => status === 'pending';

export const formatPayloadDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return formatDayShort(value) || String(value);
    return /^\d{4}-\d{2}-\d{2}/.test(String(value)) ? formatDayShort(value) || String(value) : String(value);
};

export const confidenceLabel = (score) => {
    if (typeof score !== 'number' || Number.isNaN(score)) return null;
    const pct = Math.round(score * 100);
    if (pct >= 80) return { text: `High confidence (${pct}%)`, color: 'success' };
    if (pct >= 50) return { text: `Medium confidence (${pct}%)`, color: 'warning' };
    return { text: `Low confidence (${pct}%)`, color: 'default' };
};
